import React, { useMemo, useState, useCallback } from 'react';
import { Table, Tag, Button, Modal, message, Collapse, Space, Form, Input, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, CheckCircleFilled, CloseCircleFilled, ClockCircleFilled, DownloadOutlined } from '@ant-design/icons';
import { getDataForms, getSubYear, getFieldValue, PHD_TYPES } from '../utils/dataForms';
import { formatDateTime } from '../utils/timeUtils';
import { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';
import api from '../utils/api';

function formatCellValue(v) {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '是' : '否';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Group by indicator data_type + same batch submitted_at
 */
export function groupPhdSubmissionsByIndicatorBatch(submissions, orderedForms) {
  const typeOrder = new Map((orderedForms || []).map((f, i) => [f.key, i]));
  const map = new Map();
  for (const s of submissions || []) {
    const batchKey =
      s.submitted_at != null && String(s.submitted_at).trim() !== ''
        ? String(s.submitted_at)
        : `rec:${s.id}`;
    const key = `${s.data_type}::${batchKey}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  const groups = [...map.values()].map((list) => ({
    dataType: list[0].data_type,
    submittedAt: list[0].submitted_at,
    submissions: list
  }));
  groups.sort((a, b) => {
    const oa = typeOrder.get(a.dataType) ?? 999;
    const ob = typeOrder.get(b.dataType) ?? 999;
    if (oa !== ob) return oa - ob;
    const ta = String(a.submittedAt || '');
    const tb = String(b.submittedAt || '');
    return tb.localeCompare(ta);
  });
  return groups;
}

function formLabelFor(forms, dataType) {
  return forms.find((f) => f.key === dataType)?.label || dataType;
}

/**
 * Get cell data for a submission (compatible with both old and new flows)
 */
function getCellMap(sub, fieldKeys) {
  const cellMap = {};
  if (sub.cells && sub.cells.length > 0) {
    // New flow: separate cell records
    sub.cells.forEach(cell => {
      cellMap[cell.field_key] = cell;
    });
  } else {
    // Old flow: parse from data_content
    const dc = sub.data_content || {};
    fieldKeys.forEach(fk => {
      const v = dc[fk];
      if (v !== undefined && v !== null && v !== '') {
        cellMap[fk] = { value: v, status: sub.status, rejection_reason: sub.rejection_reason };
      }
    });
  }
  return cellMap;
}

/**
 * Review cell component — displays data value, proof materials, review status, approve/reject buttons
 */
function ReviewCell({ cellInfo, fieldKey, dataType, readOnly, approvingCells, onApprove, onReject, onDownload }) {
  if (!cellInfo || cellInfo.value === '—' || cellInfo.value === null || cellInfo.value === undefined || cellInfo.value === '') {
    return <span style={{ color: '#d9d9d9' }}>—</span>;
  }
  const isPending = cellInfo.status === 'pending' || !cellInfo.status;
  const isApproved = cellInfo.status === 'approved';
  const isRejected = cellInfo.status === 'rejected';
  const hasCellId = cellInfo.cellId != null;
  const proofs = cellInfo.proof_materials || [];

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
        {cellInfo.value}
        {isApproved && <CheckCircleFilled style={{ color: '#52c41a', fontSize: 12, marginLeft: 4 }} />}
        {isRejected && <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 12, marginLeft: 4 }} />}
      </div>
      {proofs.length > 0 && (
        <div style={{ marginTop: 2, fontSize: 11 }}>
          {proofs.map((pm, idx) => (
            <div key={pm.id || idx}>
              <a
                onClick={(e) => { e.stopPropagation(); onDownload(pm.id, pm.filename); }}
                style={{ fontSize: 11, color: '#1677ff' }}
              >
                <DownloadOutlined /> {pm.filename}
              </a>
            </div>
          ))}
        </div>
      )}
      {!readOnly && hasCellId && isPending && (
        <Space size="small" style={{ marginTop: 4 }}>
          <Button
            type="primary" size="small" icon={<CheckOutlined />}
            loading={approvingCells.has(cellInfo.cellId)}
            onClick={(e) => { e.stopPropagation(); onApprove(cellInfo.cellId, cellInfo.subId, fieldKey); }}
            style={{ height: 22, fontSize: 11, padding: '0 6px' }}
          />
          <Button
            danger size="small" icon={<CloseOutlined />}
            onClick={(e) => { e.stopPropagation(); onReject(cellInfo.cellId, cellInfo.subId, fieldKey, dataType); }}
            style={{ height: 22, fontSize: 11, padding: '0 6px' }}
          />
        </Space>
      )}
      {isRejected && cellInfo.rejection_reason && (
        <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 2, maxWidth: 120, wordBreak: 'break-all' }}>
          {cellInfo.rejection_reason}
        </div>
      )}
    </div>
  );
}

/**
 * Matrix table within a single submission batch - supports cell-level review
 * Rows = task items, columns = years
 * Each cell displays value + review status + action buttons
 */
export function PhdBatchMatrix({ deptName, submissions, onCellReview, readOnly, combinedPhd = false }) {
  const [detailSub, setDetailSub] = useState(null);
  const [rejectCellInfo, setRejectCellInfo] = useState(null); // { cellId, fieldKey, submissionId, dataType }
  const [rejectForm] = Form.useForm();
  const [rejectLoading, setRejectLoading] = useState(false);
  const [approvingCells, setApprovingCells] = useState(new Set());
  const [rejectModalVisible, setRejectModalVisible] = useState(false);

  const forms = useMemo(() => getDataForms(deptName || ''), [deptName]);
  const phdForms = useMemo(
    () => (combinedPhd ? forms.filter((f) => PHD_TYPES.includes(f.key)) : []),
    [combinedPhd, forms]
  );
  const dataType = submissions?.[0]?.data_type;
  const singleForm = useMemo(
    () => (combinedPhd ? phdForms[0] : forms.find((f) => f.key === dataType)),
    [combinedPhd, phdForms, forms, dataType]
  );

  // Collect all field keys (excluding "year" and "年份")
  const fieldKeys = useMemo(() => {
    if (combinedPhd) {
      return phdForms.flatMap((f) => (f.fields || []).filter(fld => fld.key !== '年份' && fld.key !== 'year' && !fld.computed).map(fld => fld.key));
    }
    if (!singleForm) return [];
    return (singleForm.fields || [])
      .filter(f => f.key !== '年份' && f.key !== 'year' && !f.computed)
      .map(f => f.key);
  }, [combinedPhd, phdForms, singleForm]);

  // Index submissions by year
  const subsByYear = useMemo(() => {
    const m = new Map();
    (submissions || []).forEach(s => {
      const y = getSubYear(s);
      if (y != null) {
        if (!m.has(y)) m.set(y, []);
        m.get(y).push(s);
      }
    });
    return m;
  }, [submissions]);

  // Submissions without a year
  const noYearSubs = useMemo(() => {
    return (submissions || []).filter(s => getSubYear(s) == null);
  }, [submissions]);

  const years = useMemo(() => [...subsByYear.keys()].sort((a, b) => a - b), [subsByYear]);

  // Build cell map for each submission (cached)
  const cellMaps = useMemo(() => {
    const m = new Map();
    (submissions || []).forEach(s => {
      m.set(s.id, getCellMap(s, fieldKeys));
    });
    return m;
  }, [submissions, fieldKeys]);

  // Handle single cell approval
  const handleApproveCell = useCallback(async (cellId, submissionId, fieldKey) => {
    setApprovingCells(prev => new Set([...prev, cellId]));
    try {
      await api.post(`/submissions/cells/${cellId}/review`, { status: 'approved' });
      message.success(`「${fieldKey}」已通过`);
      if (onCellReview) onCellReview();
    } catch (error) {
      message.error(error.response?.data?.error || '审核失败');
    } finally {
      setApprovingCells(prev => { const next = new Set(prev); next.delete(cellId); return next; });
    }
  }, [onCellReview]);

  // Open rejection modal
  const handleRejectClick = useCallback((cellId, submissionId, fieldKey, dataType) => {
    setRejectCellInfo({ cellId, fieldKey, submissionId, dataType });
    rejectForm.resetFields();
    setRejectModalVisible(true);
  }, [rejectForm]);

  // Submit rejection
  const handleRejectConfirm = useCallback(async () => {
    try {
      const values = await rejectForm.validateFields();
      const reason = values.rejection_reason;
      if (!reason || !reason.trim()) {
        message.warning('请填写拒绝原因');
        return;
      }
      setRejectLoading(true);
      const { cellId, fieldKey } = rejectCellInfo;
      await api.post(`/submissions/cells/${cellId}/review`, {
        status: 'rejected',
        rejection_reason: reason,
      });
      message.success(`「${fieldKey}」已拒绝`);
      setRejectModalVisible(false);
      setRejectCellInfo(null);
      if (onCellReview) onCellReview();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.error || '操作失败');
    } finally {
      setRejectLoading(false);
    }
  }, [rejectForm, rejectCellInfo, onCellReview]);

  const renderCellContent = (subList, fieldKey) => {
    if (!subList || subList.length === 0) {
      return { value: '—', status: null, cellId: null, subId: null, proof_materials: [] };
    }
    // Take the latest record
    const sorted = subList.slice().sort((a, b) => (b.id || 0) - (a.id || 0));
    const sub = sorted[0];
    const cellMap = cellMaps.get(sub.id) || {};
    const cell = cellMap[fieldKey];
    if (!cell) return { value: '—', status: null, cellId: null, subId: sub.id, proof_materials: [] };
    return { value: cell.value, status: cell.status, cellId: cell.id, subId: sub.id, rejection_reason: cell.rejection_reason, proof_materials: cell.proof_materials || [] };
  };

  const dataSource = useMemo(() => {
    if (combinedPhd && phdForms.length > 0) {
      const result = [];
      phdForms.forEach((form) => {
        const startIdx = result.length;
        const filtered = (form.fields || []).filter(
          (f) => f.key !== '年份' && f.key !== 'year' && !f.computed
        );
        filtered.forEach((field) => {
          const label = field.label.startsWith('其中:') ? '  ' + field.label : field.label;
          const row = {
            key: `${form.key}::${field.key}`,
            indicatorLabel: form.label,
            dataType: form.key,
            fieldKey: field.key,
            taskItemLabel: label,
          };
          years.forEach((y) => {
            const subList = (subsByYear.get(y) || []).filter(s => s.data_type === form.key);
            row[`y-${y}`] = renderCellContent(subList, field.key);
          });
          if (noYearSubs.length > 0) {
            row['no-year'] = renderCellContent(noYearSubs.filter(s => s.data_type === form.key), field.key);
          }
          result.push(row);
        });
        // rowSpan for this indicator type group
        const count = result.length - startIdx;
        if (count > 0) {
          result[startIdx].indicatorRowSpan = count;
          for (let i = startIdx + 1; i < result.length; i++) {
            result[i].indicatorRowSpan = 0;
          }
        }
      });
      return result;
    }
    if (!singleForm) return [];
    return (singleForm.fields || [])
      .filter(f => f.key !== '年份' && f.key !== 'year' && !f.computed)
      .map((field, idx) => {
        const rows = [];
        const label = field.label.startsWith('其中:') ? '  ' + field.label : field.label;
        const row = {
          key: `${singleForm.key}::${field.key}`,
          indicatorLabel: singleForm.label,
          dataType: singleForm.key,
          fieldKey: field.key,
          taskItemLabel: label,
        };
        // Build cell data for each year
        years.forEach(y => {
          const subList = subsByYear.get(y) || [];
          const cellInfo = renderCellContent(subList, field.key);
          row[`y-${y}`] = cellInfo;
        });
        if (noYearSubs.length > 0) {
          row['no-year'] = renderCellContent(noYearSubs, field.key);
        }
        return row;
      })
      .flat();
  }, [combinedPhd, phdForms, singleForm, years, subsByYear, noYearSubs, fieldKeys]);

  // Set rowSpan for the first row (non-combined mode only)
  if (!combinedPhd && dataSource.length > 0) {
    dataSource[0].indicatorRowSpan = dataSource.length;
    for (let i = 1; i < dataSource.length; i++) dataSource[i].indicatorRowSpan = 0;
  }

  const renderYearColumnTitle = (y) => {
    return <span style={{ fontWeight: 600 }}>{String(y)}</span>;
  };

  const renderStatusTag = (cellInfo) => {
    if (!cellInfo || !cellInfo.status) return null;
    if (cellInfo.status === 'approved') {
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 14 }} title="已通过" />;
    }
    if (cellInfo.status === 'rejected') {
      return (
        <Tooltip title={cellInfo.rejection_reason || '已拒绝'}>
          <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 14 }} />
        </Tooltip>
      );
    }
    return null;
  };

  // Build columns
  const columns = singleForm ? [
    {
      title: '指标',
      key: 'indicator',
      width: 120,
      fixed: 'left',
      render: (_, r) => {
        if (r.indicatorRowSpan === 0) return { children: null, props: { rowSpan: 0 } };
        return { children: <span style={{ fontWeight: 600 }}>{r.indicatorLabel}</span>, props: { rowSpan: r.indicatorRowSpan || 1 } };
      }
    },
    {
      title: '任务项',
      key: 'task',
      width: 160,
      fixed: 'left',
      dataIndex: 'taskItemLabel',
    },
    ...years.map(y => ({
      title: renderYearColumnTitle(y),
      key: `y-${y}`,
      width: 160,
      align: 'center',
      render: (_, row) => {
        const cellInfo = row[`y-${y}`];
        return (
          <ReviewCell
            cellInfo={cellInfo}
            fieldKey={row.fieldKey}
            dataType={row.dataType}
            readOnly={readOnly}
            approvingCells={approvingCells}
            onApprove={handleApproveCell}
            onReject={handleRejectClick}
            onDownload={handleDownload}
          />
        );
      }
    })),
    ...(noYearSubs.length > 0 ? [{
      title: <span style={{ fontWeight: 600 }}>未填年份</span>,
      key: 'no-year',
      width: 160,
      align: 'center',
      render: (_, row) => {
        const cellInfo = row['no-year'];
        return (
          <ReviewCell
            cellInfo={cellInfo}
            fieldKey={row.fieldKey}
            dataType={row.dataType}
            readOnly={readOnly}
            approvingCells={approvingCells}
            onApprove={handleApproveCell}
            onReject={handleRejectClick}
            onDownload={handleDownload}
          />
        );
      }
    }] : []),
  ] : [];

  // Detail modal
  const detailForm = detailSub ? forms.find(f => f.key === detailSub.data_type) : null;
  const detailFields = detailForm?.fields || [];
  const dc = detailSub?.data_content || {};
  const detailCellMap = detailSub ? cellMaps.get(detailSub.id) || {} : {};

  const handleDownload = async (materialId, filename) => {
    try {
      const token = getToken();
      const apiUrl = getApiBaseURL();
      const response = await fetch(`${apiUrl}/proof-materials/${materialId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error((await response.json()).error || '下载失败');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || '证明材料';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      message.error(e.message || '下载失败');
    }
  };

  if (!forms.length) {
    return <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>暂无博士授权点表单配置</div>;
  }
  if (!submissions?.length) return null;
  if (!singleForm) {
    return <div style={{ padding: 16, color: '#999' }}>未知指标类型：{dataType}</div>;
  }
  if (!dataSource.length) {
    return <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>该指标无数据字段</div>;
  }

  return (
    <div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="key"
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: Math.max(500, years.length * 170 + 360) }}
      />

      {/* Detail modal */}
      <Modal title={`填报详情 · 记录 #${detailSub?.id || ''}`}
        open={!!detailSub}
        onCancel={() => setDetailSub(null)}
        footer={[<Button key="close" onClick={() => setDetailSub(null)}>关闭</Button>]}
        width={720}
        destroyOnClose
      >
        {detailSub && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Tag color={detailSub.status === 'approved' ? 'green' : detailSub.status === 'rejected' ? 'red' : 'orange'}>
                {detailSub.status === 'approved' ? '已通过' : detailSub.status === 'rejected' ? '已拒绝' : '待审核'}
              </Tag>
              {detailSub.submitted_by_username && <span style={{ marginLeft: 8 }}>提交人：{detailSub.submitted_by_username}</span>}
            </div>
            <Table size="small" pagination={false} rowKey="key"
              columns={[
                { title: '字段', dataIndex: 'label', width: '42%' },
                { title: '值', dataIndex: 'value' },
                { title: '审核状态', dataIndex: 'status' },
              ]}
              dataSource={[
                ...detailFields.filter(f => !f.computed && f.key !== '年份').map(f => {
                  const cell = detailCellMap[f.key] || {};
                  return {
                    key: f.key,
                    label: f.label,
                    value: dc[f.key] !== undefined && dc[f.key] !== null && dc[f.key] !== '' ? String(dc[f.key]) : '—',
                    status: cell.status ? (cell.status === 'approved' ? '✅ 已通过' : cell.status === 'rejected' ? `❌ 已拒绝${cell.rejection_reason ? ': ' + cell.rejection_reason : ''}` : '⏳ 待审核') : (detailSub.status === 'pending' ? '⏳ 待审核' : '—'),
                  };
                })
              ]}
            />
          </>
        )}
      </Modal>

      {/* Rejection reason modal */}
      <Modal
        title="填写拒绝原因"
        open={rejectModalVisible}
        onCancel={() => { setRejectModalVisible(false); setRejectCellInfo(null); }}
        onOk={handleRejectConfirm}
        confirmLoading={rejectLoading}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 12 }}>
          <Tag color="blue">{rejectCellInfo?.dataType}</Tag>
          <span style={{ fontWeight: 'bold' }}>{rejectCellInfo?.fieldKey}</span>
        </div>
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejection_reason"
            label="拒绝原因"
            rules={[{ required: true, message: '请填写拒绝原因' }]}
          >
            <Input.TextArea rows={4} placeholder="请说明拒绝原因，以便提交方修改后重新提交" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

/**
 * Main PhD review matrix component: collapsed display by indicator x submission batch, supports cell-level review
 * When the department is a PhD authorization point, all PHD types are merged into a single combined table
 */
const PhdReviewMatrix = ({ deptName, submissions, onCellReview, readOnly }) => {
  const forms = useMemo(() => getDataForms(deptName || ''), [deptName]);
  const hasPhdForms = useMemo(
    () => forms.some((f) => PHD_TYPES.includes(f.key)),
    [forms]
  );
  const groups = useMemo(
    () => hasPhdForms ? [] : groupPhdSubmissionsByIndicatorBatch(submissions || [], forms),
    [hasPhdForms, submissions, forms]
  );

  if (!forms.length) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>暂无博士授权点表单配置</div>;
  }
  if (!submissions || submissions.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>暂无记录</div>;
  }

  // PhD authorization point department: merge directly into a single combined table
  if (hasPhdForms) {
    return (
      <PhdBatchMatrix
        deptName={deptName}
        submissions={submissions}
        onCellReview={onCellReview}
        readOnly={readOnly}
        combinedPhd
      />
    );
  }

  if (groups.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>暂无记录</div>;
  }

  const collapseItems = groups.map((g, idx) => {
    const pending = g.submissions.filter((s) => s.status === 'pending').length;
    const label = formLabelFor(forms, g.dataType);
    const timeStr = g.submittedAt ? formatDateTime(g.submittedAt) : '时间未知（逐条提交）';
    return {
      key: `phd-batch-${idx}-${g.dataType}-${g.submissions[0]?.id}`,
      label: (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <Tag color="blue">{label}</Tag>
          <span style={{ color: '#666' }}>提交 {timeStr}</span>
          <span>共 <strong>{g.submissions.length}</strong> 条</span>
          {pending > 0 && <Tag color="orange">待审核 {pending}</Tag>}
        </div>
      ),
      children: (
        <PhdBatchMatrix
          deptName={deptName}
          submissions={g.submissions}
          onCellReview={onCellReview}
          readOnly={readOnly}
        />
      )
    };
  });

  return (
    <div>
      <Collapse bordered items={collapseItems} defaultActiveKey={[]} />
    </div>
  );
};

export default PhdReviewMatrix;
